import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import {
  BRAND,
  SSO_ATTEMPT_KEY,
  adminReturnToFromHref,
  beginSso,
  initializeAdminAuth,
  isSsoEnabled,
  logoutAdmin,
} from "./api.js";
import { LoginPage } from "./pages/Login.js";
import { DashboardPage } from "./pages/Dashboard.js";
import { UsersPage } from "./pages/Users.js";
import { ModerationPage } from "./pages/Moderation.js";
import { PendingIngredientsPage } from "./pages/PendingIngredients.js";
import { TranslationsPage } from "./pages/Translations.js";
import { FlagsPage } from "./pages/Flags.js";
import { SubscriptionsPage } from "./pages/Subscriptions.js";
import { AuditLogsPage } from "./pages/AuditLogs.js";
import { AnalyticsPage } from "./pages/Analytics.js";
import { ReleaseGatesPage } from "./pages/ReleaseGates.js";
import { WorkersPage } from "./pages/Workers.js";
import { FeedbackPage } from "./pages/Feedback.js";

export function App() {
  const [authState, setAuthState] = useState<"checking" | "authenticated" | "login">("checking");
  const [ssoEnabled, setSsoEnabled] = useState(false);

  const currentReturnTo = () => adminReturnToFromHref(window.location.href);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (await initializeAdminAuth()) {
        if (!cancelled) setAuthState("authenticated");
        return;
      }
      const enabled = await isSsoEnabled();
      if (cancelled) return;
      setSsoEnabled(enabled);
      const ssoError = new URLSearchParams(window.location.search).get("ssoError");
      if (enabled && !ssoError && !sessionStorage.getItem(SSO_ATTEMPT_KEY)) {
        sessionStorage.setItem(SSO_ATTEMPT_KEY, "1");
        beginSso(currentReturnTo());
        return;
      }
      setAuthState("login");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (authState === "checking") {
    return (
      <div className="login-wrap" role="status" aria-live="polite">
        <div className="login-box">
          <strong>Kontrollerar AAMOS Login …</strong>
          <span className="muted">Du skickas vidare automatiskt när sessionen är klar.</span>
        </div>
      </div>
    );
  }

  if (authState === "login") {
    return (
      <LoginPage
        ssoEnabled={ssoEnabled}
        ssoError={new URLSearchParams(window.location.search).get("ssoError")}
        onSso={() => {
          sessionStorage.setItem(SSO_ATTEMPT_KEY, "1");
          beginSso(currentReturnTo());
        }}
        onLogin={() => setAuthState("authenticated")}
      />
    );
  }

  return (
    <div className="layout">
      <nav className="sidebar">
        <h1>{BRAND.name} Admin</h1>
        <NavLink to="/" end>
          Översikt
        </NavLink>
        <NavLink to="/users">Användare</NavLink>
        <NavLink to="/moderation">Moderering</NavLink>
        <NavLink to="/pending-ingredients">Nya ingredienser</NavLink>
        <NavLink to="/translations">Översättningar</NavLink>
        <NavLink to="/feedback">Felanmälningar</NavLink>
        <NavLink to="/flags">Feature flags</NavLink>
        <NavLink to="/subscriptions">Prenumerationer</NavLink>
        <NavLink to="/audit">Audit logs</NavLink>
        <NavLink to="/analytics">Analytics</NavLink>
        <NavLink to="/release-gates">Release gates</NavLink>
        <NavLink to="/workers">Workers</NavLink>
        <button
          onClick={() => {
            void logoutAdmin().finally(() => setAuthState("login"));
          }}
        >
          Logga ut
        </button>
      </nav>
      <main className="main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/moderation" element={<ModerationPage />} />
          <Route path="/pending-ingredients" element={<PendingIngredientsPage />} />
          <Route path="/translations" element={<TranslationsPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/flags" element={<FlagsPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/audit" element={<AuditLogsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/release-gates" element={<ReleaseGatesPage />} />
          <Route path="/workers" element={<WorkersPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
